/* eslint-disable react/display-name */
import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertMenu from 'components/@common/MoreVertMenu';
import ProductIconList from 'components/@common/ProductIconList';
import StatusChip from 'components/@common/StatusChip';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { ActionOption } from 'types';
import { User } from 'types/schema-generated-types';
import { formatDateString } from 'utilities/date-utils';
import { getFullName, getInitials } from 'utilities/name-utils';
import { commaDelimit } from 'utilities/string-utils';
import getOrgAssociationDetails from '../utils/get-org-association-details';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(5),
    paddingTop: 0
  },
  table: {
    maxWidth: 'unset !important'
  },
  tableBody: {
    background: 'white'
  },
  tableRow: {
    width: 30
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  avatar: {
    height: 44,
    width: 44
  },
  productImage: {
    height: 26,
    width: 26
  },
  tableActionColumn: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(2)
  },
  actionButton: {
    height: 30
  }
}));

export interface UsersListViewContainerProps {
  users: User[];
  actionOptions?: ActionOption[];
}

const UsersListViewContainer: React.FC<UsersListViewContainerProps> = ({
  users,
  actionOptions
}) => {
  const classes = useStyles();
  const { t } = useTranslation('UsersListViewContainer');

  const columns: ColumnProps<any>[] = useMemo(
    () => [
      {
        id: 'avatar',
        label: '',
        getValue: item => (
          <div key={`${item.id}_col_0`} className={classes.avatarContainer}>
            <Avatar alt={item.fullName} className={classes.avatar} src={item.avatarUrl || ''}>
              {item.initials}
            </Avatar>
          </div>
        ),
        sortable: false
      },
      {
        id: 'name',
        label: t('Name'),
        getValue: (item): ReactElement => <div key={`${item.id}_col_1`}>{item.fullName}</div>,
        sortKey: 'fullName',
        sortable: true
      },
      {
        id: 'pin',
        label: t('Educator PIN'),
        getValue: (item): ReactElement => (
          <div key={`${item.id}_col_2`}>
            <Typography>{item.pin}</Typography>
          </div>
        ),
        sortKey: 'pin',
        sortable: true
      },
      {
        id: 'organization',
        label: t('Organization'),
        getValue: (item): ReactElement => (
          <div key={`${item.id}_col_3`}>
            <Typography>{item.organization}</Typography>
          </div>
        ),
        sortKey: 'organization',
        sortable: true
      },
      {
        id: 'roles',
        label: t('Roles'),
        getValue: (item): ReactElement => (
          <div key={`${item.id}_col_4`}>
            <Typography>{item.organizationRoles}</Typography>
          </div>
        ),
        sortKey: 'roles',
        sortable: true
      },
      {
        id: 'products',
        label: t('Products Used'),
        getValue: (item): ReactElement => (
          <div key={`${item.id}_col_5`}>
            <ProductIconList style={{ minWidth: 120 }} licenseUsage={item.licenseUsage} />
          </div>
        ),
        sortable: false
      },
      {
        id: 'lastActivity',
        label: t('Last Activity'),
        getValue: (item): ReactElement => (
          <div key={`${item.id}_col_6`}>
            <Typography>{formatDateString(item.dateUpdated)}</Typography>
          </div>
        ),
        sortKey: 'dateUpdatedMilli',
        sortable: true
      },
      {
        id: 'status',
        label: t('Status'),
        getValue: (item): ReactElement => (
          <div key={`${item.id}_col_7`}>
            <StatusChip status={item.status} size="small" />
          </div>
        ),
        sortKey: 'status',
        sortable: true
      },
      {
        id: 'actions',
        label: '',
        // eslint-disable-next-line react/display-name
        getValue(item: User): ReactElement {
          return (
            <MoreVertMenu
              actionOptions={actionOptions ?? []}
              id={item.id}
              context={{
                organizationId: item.organizationAssociations?.map(a => a.organization.id)
              }}
            />
          );
        },
        sortable: false
      }
    ],
    [actionOptions, classes, t]
  );

  // TODO: Maybe rework to pass users array directly into table's 'data' prop?
  const data = useMemo(
    () =>
      users.map(user => {
        const { organizationNames, organizationRoleNames } = getOrgAssociationDetails(user);
        const updateDateObject = new Date(user.dateUpdated);

        return {
          id: user.id,
          fullName: getFullName(user),
          initials: getInitials(user),
          pin: user.pin,
          organization: commaDelimit(organizationNames),
          organizationRoles: commaDelimit(organizationRoleNames),
          dateUpdated: updateDateObject,
          dateUpdatedMilli: updateDateObject.getTime(),
          status: user.status,
          licenseUsage: user.licenseUsage
        };
      }),
    [users]
  );

  return (
    <div className={classes.container}>
      <LwtTable
        lazyLoadingLimit={50}
        columns={columns}
        data={data}
        options={{ checkbox: false, sortable: true }}
        classes={{
          table: () => classes.table,
          body: () => classes.tableBody,
          row: () => classes.tableRow
        }}
      />
    </div>
  );
};

export default UsersListViewContainer;
